/**
 * Simple cache utility for API responses
 * Uses sessionStorage to persist cache across page navigations within the same session.
 *
 * User-scoped: cache keys include the current user ID to prevent data leakage
 * between different user sessions (login/logout, impersonation).
 */

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expiresIn: number; // milliseconds
}

const CACHE_PREFIX = 'helix_cache_';

// Current user scope — set on login, cleared on logout
let userScope: string | number | null = null;

/**
 * Build the full storage key, scoped to the current user when available.
 */
const buildKey = (key: string): string => {
  if (userScope) {
    return `${CACHE_PREFIX}u${userScope}_${key}`;
  }
  return `${CACHE_PREFIX}${key}`;
};

export class Cache {
  /**
   * Set the current user scope for cache isolation.
   * Call this on login/impersonation to ensure cache keys are user-specific.
   */
  static setUserScope(userId: string | number | null): void {
    userScope = userId;
  }

  /**
   * Store data in cache with expiry time
   * @param key - Cache key
   * @param data - Data to cache
   * @param expiresIn - Expiry time in milliseconds (default: 5 minutes)
   */
  static set<T>(key: string, data: T, expiresIn: number = 5 * 60 * 1000): void {
    try {
      const entry: CacheEntry<T> = {
        data,
        timestamp: Date.now(),
        expiresIn,
      };
      sessionStorage.setItem(buildKey(key), JSON.stringify(entry));
    } catch (error) {
      console.warn('Failed to set cache:', error);
    }
  }

  /**
   * Get data from cache if not expired
   * @param key - Cache key
   * @returns Cached data or null if expired/not found
   */
  static get<T>(key: string): T | null {
    try {
      const item = sessionStorage.getItem(buildKey(key));
      if (!item) return null;

      const entry: CacheEntry<T> = JSON.parse(item);
      const now = Date.now();

      // Check if cache is expired
      if (now - entry.timestamp > entry.expiresIn) {
        // Cache expired, remove it
        this.remove(key);
        return null;
      }

      return entry.data;
    } catch (error) {
      console.warn('Failed to get cache:', error);
      return null;
    }
  }

  /**
   * Remove specific cache entry
   * @param key - Cache key
   */
  static remove(key: string): void {
    try {
      sessionStorage.removeItem(buildKey(key));
    } catch (error) {
      console.warn('Failed to remove cache:', error);
    }
  }

  /**
   * Clear all cache entries (all users)
   */
  static clearAll(): void {
    try {
      const keys = Object.keys(sessionStorage);
      keys.forEach(key => {
        if (key.startsWith(CACHE_PREFIX)) {
          sessionStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.warn('Failed to clear cache:', error);
    }
  }

  /**
   * Check if cache exists and is valid
   * @param key - Cache key
   * @returns true if cache exists and is not expired
   */
  static has(key: string): boolean {
    return this.get(key) !== null;
  }
}
